/* eslint-disable no-underscore-dangle */
import {
  BaseField,
  DisplayIf,
  Field,
  FormLayout,
  HookForm,
} from '@/components_v2/HookForm';
import { QuestionTypes, TriggerableTypes } from '@/gql/graphql';
import { useModals } from '@/hooks/ModalManager';
import { Button, chakra } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';

import {
  AutomationStepsSelect,
  FormularySelect,
  OptionSelect,
  QuestionSelect,
} from '../../Selects';
import { triggerSchema, TriggerSchema } from '../form-schemas';
import { Callout } from '../misc';


const getTriggerableTypes = (t: any) => [
  { value: '', label: t('placeholders.select') },
  { value: TriggerableTypes.Formulary, label: t('form_types.form') },
  { value: TriggerableTypes.Question, label: t('form_types.question') },
  { value: TriggerableTypes.Option, label: t('form_types.option') },
  { value: TriggerableTypes.Step, label: t('form_types.step') },
];

function Fields({
  automationFlowId,
  triggerIds,
  currentTriggerableIds,
}: {
  automationFlowId: string;
  triggerIds: string[];
  currentTriggerableIds: string[];
}) {
  const { t } = useTranslation();
  const form = useFormContext<TriggerSchema>();

  const triggerable = form.watch('triggerable');
  const triggerableType = form.watch('triggerableType');
  const warningOnlyValue = form.watch('warningOnly');

  const selectedQuestion = triggerable?.[0] as any;
  const canShowWarningOnly = selectedQuestion?.data?.hasAlert ?? false;

  const shouldShowWarningOnly = !!warningOnlyValue || canShowWarningOnly;

  return (
    <FormLayout padding={1} spacing={5} flexGrow={0} w="full">
      <chakra.div display="none">
        <Field<TriggerSchema> name="id" />
        <Field<TriggerSchema> name="_key" />
      </chakra.div>
      <Callout>
        <chakra.p fontSize="xl" fontWeight="bold">
          {t('callouts.how_to_use')}
        </chakra.p>
        {t('callouts.activation_description')}{' '}
        <strong>
          {t('callouts.activation_types')}
        </strong>
      </Callout>
      <Field<TriggerSchema>
        name="triggerableType"
        label={t('form_labels.activation_type')}
        type="native-select"
        defaultValue=""
        onChange={() => {
          form.resetField('triggerable');
        }}
        options={getTriggerableTypes(t)}
      />

      <chakra.div>
        <DisplayIf<TriggerSchema>
          name="triggerableType"
          condition={(value) => value === TriggerableTypes.Formulary}
        >
          <Controller
            name="triggerable"
            control={form.control}
            shouldUnregister
            render={({ field }) => (
              <BaseField
                name="triggerable"
                label={t('form_labels.activation_form')}
                flexGrow={0}
                marginBottom={4}
              >
                <FormularySelect
                  selectionMode="single"
                  triggerIds={triggerIds}
                  currentTriggerableIds={currentTriggerableIds}
                  value={field.value?.map((e) => e.id) as any}
                  initialOptions={(field.value || []) as any}
                  onChange={(keys) => {
                    field.onChange(keys);
                  }}
                />
              </BaseField>
            )}
          />

          <Field<TriggerSchema>
            name="flaggedOnly"
            label={t('form_labels.flagged_only')}
            type="switch"
          />
        </DisplayIf>

        <DisplayIf<TriggerSchema>
          name="triggerableType"
          condition={(value) => value === TriggerableTypes.Question}
        >
          <chakra.div>
            <Controller
              name="triggerable"
              control={form.control}
              shouldUnregister
              render={({ field }) => (
                <BaseField
                  name="triggerable"
                  label={t('form_labels.activation_question')}
                  flexGrow={0}
                  marginBottom={4}
                >
                  <QuestionSelect
                    selectionMode="single"
                    triggerIds={triggerIds}
                    currentTriggerableIds={currentTriggerableIds}
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              )}
            />

            {shouldShowWarningOnly && (
              <Field<TriggerSchema>
                name="warningOnly"
                label={t('form_labels.warning_only')}
                type="switch"
              />
            )}
          </chakra.div>
        </DisplayIf>

        <DisplayIf<TriggerSchema>
          name="triggerableType"
          condition={(value) => value === TriggerableTypes.Option}
        >
          <Controller
            name="triggerable"
            control={form.control}
            shouldUnregister
            render={({ field }) => {
              return (
                <BaseField
                  name="triggerable"
                  label={t('form_labels.activation_option')}
                  flexGrow={0}
                >
                  <OptionSelect
                    selectionMode="single"
                    triggerIds={triggerIds}
                    currentTriggerableIds={currentTriggerableIds}
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              );
            }}
          />
        </DisplayIf>
        <DisplayIf<TriggerSchema>
          name="triggerableType"
          condition={(value) => value === TriggerableTypes.Step}
        >
          <Controller
            name="triggerable"
            control={form.control}
            shouldUnregister
            render={({ field }) => {
              return (
                <BaseField
                  name="triggerable"
                  label={t('form_labels.activation_step')}
                  flexGrow={0}
                >
                  <AutomationStepsSelect
                    selectionMode="single"
                    triggerIds={triggerIds}
                    currentTriggerableIds={currentTriggerableIds}
                    automationFlowId={automationFlowId}
                    value={field.value?.map((e) => e?.id) as any}
                    initialOptions={(field.value || []) as any}
                    onChange={(keys) => {
                      field.onChange(keys);
                    }}
                  />
                </BaseField>
              );
            }}
          />
        </DisplayIf>
      </chakra.div>

      <DisplayIf<TriggerSchema>
        name="triggerableType"
        condition={(value) => value === TriggerableTypes.Option}
      >
        <DisplayIf<TriggerSchema>
          name="triggerable"
          condition={(value) => !!value}
        >
          <chakra.div display="flex" flexDir="column" gap="4">
            <Field
              type="radio"
              name="delayType"
              label={t('form_labels.activation_type')}
              size="lg"
              defaultValue="immediate"
              options={[
                { value: 'immediate', label: t('step_options.immediate') },
                {
                  value: 'delay',
                  label: t('step_options.delay'),
                },
              ]}
            />
            <DisplayIf<TriggerSchema>
              name="delayType"
              condition={(value) => value === 'delay'}
            >
              <Controller
                name="dateQuestion"
                control={form.control}
                shouldUnregister
                render={({ field }) => (
                  <BaseField
                    name="dateQuestion"
                    label={t('form_labels.inherit_date_question')}
                    flexGrow={0}
                  >
                    <QuestionSelect
                      selectionMode="single"
                      questionTypes={[
                        QuestionTypes.Date,
                        QuestionTypes.DateSla,
                      ]}
                      parentOptionId={
                        triggerableType === TriggerableTypes.Option &&
                          !!triggerable.length
                          ? triggerable[0].id
                          : undefined
                      }
                      value={field.value?.map((e) => e?.id) as any}
                      initialOptions={(field.value || []) as any}
                      onChange={(keys) => {
                        field.onChange(keys?.length ? keys : undefined);
                      }}
                    />
                  </BaseField>
                )}
              />
            </DisplayIf>
          </chakra.div>
        </DisplayIf>
      </DisplayIf>

      <DisplayIf<TriggerSchema>
        name="triggerableType"
        condition={(value) => !!value}
      >
        <Field<TriggerSchema>
          name="label"
          label={t('form_labels.activation_label')}
          type="text"
          placeholder={t('placeholders.type_dots')}
          help={t('form_labels.label_help_activation')}
        />
      </DisplayIf>
    </FormLayout>
  );
}

export type TriggerFormProps = {
  onSubmit?: (value: TriggerSchema) => void;
  automationFlowId: string;
  triggerIds?: string[];
  currentTriggerableIds?: string[];
  intitialValues?: TriggerSchema;
};
export function TriggerForm({
  intitialValues,
  onSubmit,
  automationFlowId,
  triggerIds,
  currentTriggerableIds,
}: TriggerFormProps) {
  return (
    <HookForm<TriggerSchema>
      autoComplete="off"
      overflowY="auto"
      overflowX="clip"
      position="relative"
      p="4"
      display="grid"
      flexGrow={0}
      w="full"
      id="trigger-form"
      resolver={yupResolver(triggerSchema)}
      schema={triggerSchema}
      defaultValues={intitialValues}
      onSubmit={(values) => onSubmit?.(values as TriggerSchema)}
    >
      <Fields
        automationFlowId={automationFlowId}
        triggerIds={triggerIds || []}
        currentTriggerableIds={currentTriggerableIds || []}
      />
    </HookForm>
  );
}

export function useTriggerForm({
  onSubmit,
  automationFlowId,
  currentTriggerableIds = [],
}: TriggerFormProps) {
  const modals = useModals();
  const { t } = useTranslation();

  const _onSubmit = React.useCallback(
    (value: TriggerSchema) => {
      onSubmit?.(value);
      modals.close('trigger-form');
    },
    [modals, onSubmit],
  );

  return (intitialValues?: TriggerSchema) => {
    const _currentTriggerableIds = intitialValues
      ? currentTriggerableIds.filter(
        (id) =>
          !(intitialValues as any).triggerable?.some((t: any) => t.id === id),
      )
      : currentTriggerableIds;

    modals.open({
      id: 'trigger-form',
      title: t('actions.configure_activation'),
      scope: 'alert',
      body: (
        <TriggerForm
          automationFlowId={automationFlowId}
          triggerIds={intitialValues ? [intitialValues.id] : []}
          currentTriggerableIds={_currentTriggerableIds}
          onSubmit={_onSubmit}
          intitialValues={intitialValues}
        />
      ),
      size: 'lg',
      closeOnEsc: false,
      closeOnOverlayClick: false,
      footer: (
        <Button
          type="submit"
          form="trigger-form"
          w="full"
          size="md"
          colorScheme="brand"
        >
          {t('common.save')}
        </Button>
      ),
    });
  };
}
